/** 非自定义协议可选的标准参数。选中后按协议类型带入起始字节和解析方式。 */
export const PROTOCOL_STAT_PARAM_NAMES = [
  { name: '标况总量', unit: 'Nm³' },
  { name: '工况总量', unit: 'm³' },
  { name: '温度', unit: '℃' },
  { name: '压力', unit: 'Kpa' },
  { name: '标况瞬时', unit: 'Nm³/h' },
  { name: '工况瞬时', unit: 'm³/h' }
]

const CITY_PARAM_PRESETS = {
  标况总量: { unit: 'Nm³', dataType: 'numeric', startByte: 3, byteCount: 4, parseMethod: 'UINT32_BE', calcFormula: '' },
  工况总量: { unit: 'm³', dataType: 'numeric', startByte: 7, byteCount: 4, parseMethod: 'UINT32_BE', calcFormula: '' },
  温度: { unit: '℃', dataType: 'numeric', startByte: 11, byteCount: 4, parseMethod: 'FLOAT_BE', calcFormula: '' },
  压力: { unit: 'Kpa', dataType: 'numeric', startByte: 15, byteCount: 4, parseMethod: 'FLOAT_BE', calcFormula: '' },
  标况瞬时: { unit: 'Nm³/h', dataType: 'numeric', startByte: 19, byteCount: 4, parseMethod: 'FLOAT_BE', calcFormula: '' },
  工况瞬时: { unit: 'm³/h', dataType: 'numeric', startByte: 23, byteCount: 4, parseMethod: 'FLOAT_BE', calcFormula: '' }
}

const LOW_POWER_PARAM_PRESETS = {
  标况总量: { unit: 'Nm³', dataType: 'numeric', startByte: 8, byteCount: 4, parseMethod: 'UINT32_LE', calcFormula: '' },
  工况总量: { unit: 'm³', dataType: 'numeric', startByte: 12, byteCount: 4, parseMethod: 'UINT32_LE', calcFormula: '' },
  标况瞬时: { unit: 'Nm³/h', dataType: 'numeric', startByte: 16, byteCount: 4, parseMethod: 'FLOAT_LE', calcFormula: '' },
  工况瞬时: { unit: 'm³/h', dataType: 'numeric', startByte: 20, byteCount: 4, parseMethod: 'FLOAT_LE', calcFormula: '' },
  压力: { unit: 'Kpa', dataType: 'numeric', startByte: 24, byteCount: 4, parseMethod: 'FLOAT_LE', calcFormula: '' },
  温度: { unit: '℃', dataType: 'numeric', startByte: 28, byteCount: 2, parseMethod: 'INT16_LE', calcFormula: 'x/100' }
}

export function unitForParamName(name) {
  const found = PROTOCOL_STAT_PARAM_NAMES.find(item => item.name === name)
  return found ? found.unit : ''
}

export function paramNameSelectOptions(currentName) {
  const options = PROTOCOL_STAT_PARAM_NAMES.map(item => ({ ...item }))
  const text = currentName == null ? '' : String(currentName).trim()
  if (text && !options.some(item => item.name === text)) {
    options.push({ name: text, unit: '' })
  }
  return options
}

export function protocolParamMetricKey(name) {
  const text = String(name || '').trim()
  if (!text) return ''
  const lower = text.toLowerCase()
  if (lower === 'stdtotal' || text.includes('标况总量') || text.includes('标况累积')) return 'stdTotal'
  if (lower === 'worktotal' || text.includes('工况总量') || text.includes('工况累积')) return 'workTotal'
  if (lower === 'stdinstant' || text.includes('标况瞬时')) return 'stdInstant'
  if (lower === 'workinstant' || text.includes('工况瞬时')) return 'workInstant'
  if (lower === 'temperature' || text === '温度' || text.endsWith('温度')) return 'temperature'
  if (lower === 'signalstrength' || lower === 'intensity' || lower === 'rssi' || text.includes('信号')) return 'signalStrength'
  if (lower === 'batterylevel' || lower === 'battery' || text.includes('电池电量') || text === '电量') return 'batteryLevel'
  if (lower === 'leakage' || lower === 'concentration' || text.includes('泄漏') || text.includes('泄露') || text.includes('浓度')) return 'leakage'
  if (lower === 'inletpressure' || text.includes('进口压力')) return 'inletPressure'
  if (lower === 'outletpressure2' || text.includes('出口压力2') || text.includes('出口压力二')) return 'outletPressure2'
  if (lower === 'outletpressure3' || text.includes('出口压力3') || text.includes('出口压力三')) return 'outletPressure3'
  if (lower === 'outletpressure' || lower === 'outletpressure1' || text.includes('出口压力')) return 'outletPressure'
  if (lower === 'diffpressure1' || text.includes('差压1') || text.includes('差压一')) return 'diffPressure1'
  if (lower === 'diffpressure2' || text.includes('差压2') || text.includes('差压二')) return 'diffPressure2'
  if (lower === 'pressure' || lower === 'press' || text.includes('流量计压力') || text === '压力') return 'pressure'
  return text
}

export function isCustomProtocolType(protocol) {
  return String((protocol && protocol.protocolType) || '').includes('自定义')
}

/** 非自定义协议选中标准参数名时，返回要写入表单的起始字节、字节数、单位和解析方式。 */
export function standardParamPreset(protocol, name) {
  const text = String(name || '').trim()
  if (!text || !protocol || isCustomProtocolType(protocol)) return null
  const comm = String(protocol.commType || '').toLowerCase()
  const mode = String(protocol.applyMode || '').toLowerCase()
  if (comm === 'mqtt' || mode === 'mqtt') return null
  const type = String(protocol.protocolType || '')
  const table = type.includes('低功耗') ? LOW_POWER_PARAM_PRESETS : CITY_PARAM_PRESETS
  const preset = table[text]
  return preset ? { ...preset } : null
}

export function protocolMatchesDeviceType(protocol, deviceType) {
  const type = String(deviceType || '').trim()
  if (!protocol || !type) return false
  const pt = String(protocol.protocolType || '').trim()
  const mqtt = String(protocol.applyMode || '').toLowerCase() === 'mqtt'
    || String(protocol.commType || '').toUpperCase() === 'MQTT'
  if (pt === type) return true
  if (type.includes('自定义') && (pt === '自定义类型' || pt.includes('自定义'))) return true
  if ((type === '市电远传' || type === '压力远传') && (pt === '市电压力远传' || pt === '市电远传压力')) return true
  return mqtt
}

export function protocolsForDeviceType(protocols, deviceType) {
  return (protocols || []).filter(item => protocolMatchesDeviceType(item, deviceType))
}

/** 市电压力远传：40091 起是压力远传参数，之前是市电远传参数。 */
export const CITY_POWER_PRESSURE_SPLIT_REGISTER = 40091

export function isCityPowerPressureProtocol(protocol) {
  const type = String((protocol && protocol.protocolType) || '').trim()
  return type === '市电压力远传' || type === '市电远传压力'
}

export function paramMatchesDeviceView(param, deviceType, protocol) {
  if (!isCityPowerPressureProtocol(protocol)) return true
  const view = String(deviceType || '').trim()
  if (view !== '市电远传' && view !== '压力远传') return true
  const start = Number(param && param.startByte)
  const pressureSide = Number.isFinite(start) && start >= CITY_POWER_PRESSURE_SPLIT_REGISTER
  return view === '压力远传' ? pressureSide : !pressureSide
}

export function protocolParamsToMetrics(params, deviceType, protocol) {
  const seen = new Set()
  return (params || []).map(p => {
    if (!paramMatchesDeviceView(p, deviceType, protocol)) return null
    const name = ((p && p.paramName) || '').trim()
    if (!name || name === '状态' || String((p && p.jsonName) || '').toLowerCase() === 'fstate') return null
    const key = protocolParamMetricKey(name)
    if (!key || seen.has(key)) return null
    seen.add(key)
    return { key, label: name, unit: (p && p.unit) || '', width: 130 }
  }).filter(Boolean)
}
